import { action, getTopController } from '@reatom/framework'

import { Result } from '@blade/std-lib'

import { Error } from '../core'

import { ApiError } from './ApiError'
import { AuthErrorCode } from './api-error-code'

import type { HttpResponse } from '@blade/trade-api-client'
import type { Ctx } from '@reatom/framework'

type ExtractApiError<RawError extends { code?: string }> = RawError extends {
  code: string
}
  ? ApiError<RawError['code'] | (string & {})>
  : ApiError

/**
 * Structural subset of the generated clients' `RequestParams`. `apiRequest`
 * only ever supplies `signal`, and every client declares its request params as
 * optional, so this stays assignable to all of them — unlike importing
 * `RequestParams` from one client, whose `ContentType` is nominally
 * incompatible with the others'.
 */
type ApiRequestOptions = Pick<RequestInit, 'signal'>

// eslint-disable-next-line @reatom/unit-naming-rule
export const apiErrorEvent = action<ApiError>('apiErrorEvent')

export const apiRequest = async <
  Params extends Array<unknown>,
  Data,
  Error extends object,
>(
  ctx: Ctx,
  method: (
    ...params: [...Params, ApiRequestOptions]
  ) => Promise<HttpResponse<Data, Error>>,
  // `[...Params]` (rather than `Params`) forces tuple inference, so each
  // positional argument keeps its own type instead of widening to an array.
  params: [...Params],
  options?: {
    /**
     * Whose cancellation the request follows.
     *
     * `'caller'`, the default, is the calling atom's controller: the fetch is
     * cancelled when that atom is unsubscribed or re-runs.
     *
     * `'none'` detaches it, and is for a request several callers await. A
     * fetch shared between two atoms must not die because the first of them
     * unmounted — the one still waiting would get an abort it has no way to
     * explain. The caller's own state is unaffected either way: its
     * `ctx.schedule` still throws on abort, so it restores its previous state
     * exactly as before. Only the request itself is left to finish and be
     * thrown away.
     */
    abort?: 'caller' | 'none'
  },
): Promise<
  Result.Result<Data, ExtractApiError<Error> | Error.UnknownError>
> => {
  const requestParams: ApiRequestOptions = {
    signal:
      options?.abort === 'none'
        ? undefined
        : getTopController(ctx.cause)?.signal,
  }
  try {
    const response = await method(...params, requestParams)

    if (response.ok) {
      return Result.succeed(response.data)
    }

    const code = 'code' in response.error ? response.error.code : undefined
    if (typeof code === 'string' && code) {
      const apiError = new ApiError({ code }) as ExtractApiError<Error>
      apiErrorEvent(ctx, apiError)

      return Result.fail(apiError)
    }

    return Result.fail(Error.UnknownError.fromUnknown(response.error))
  } catch (error) {
    // Generator throws non 200 responses as errors
    if (error instanceof Response) {
      if (error.status === 401) {
        const apiError = new ApiError({
          code: AuthErrorCode.Unauthorized,
        }) as ExtractApiError<Error>
        apiErrorEvent(ctx, apiError)

        return Result.fail(apiError)
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const code = (error as any)?.error?.code
      if (typeof code === 'string' && code) {
        const apiError = new ApiError({ code }) as ExtractApiError<Error>
        apiErrorEvent(ctx, apiError)

        return Result.fail(apiError)
      }
    }
    // Fetch specific exceptions
    // https://developer.mozilla.org/en-US/docs/Web/API/Window/fetch#exceptions
    return Result.fail(Error.UnknownError.fromUnknown(error))
  }
}
